class CustomerResultCards extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.track = this.querySelector('[data-result-cards-track]');
    this.cards = [...this.querySelectorAll('[data-result-card]')];
    this.currentIndex = 0;
    this.timer = null;
    this.isVisible = true;
    this.scrollFrame = null;

    if (!this.track || this.cards.length < 2) return;

    this.onScroll = () => {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateCurrentIndex());
    };

    this.onResize = () => {
      this.updateCurrentIndex();
      this.restart();
    };

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('touchstart', () => this.stop(), { passive: true });
    this.addEventListener('touchend', () => this.restart(), { passive: true });
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.start();
    });
    window.addEventListener('resize', this.onResize);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) {
            this.start();
          } else {
            this.stop();
          }
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this);
    }

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.observer?.disconnect();
  }

  canScroll() {
    return this.track.scrollWidth > this.track.clientWidth + 2;
  }

  updateCurrentIndex() {
    const trackLeft = this.track.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.currentIndex = closestIndex;
  }

  goTo(index) {
    const safeIndex = Math.max(0, Math.min(this.cards.length - 1, index));
    const card = this.cards[safeIndex];
    if (!card) return;

    const left = card.offsetLeft - this.track.offsetLeft;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollTo({
      left,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
    this.currentIndex = safeIndex;
  }

  start() {
    if (
      this.dataset.autoplay !== 'true' ||
      !this.isVisible ||
      !this.canScroll() ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    this.stop();
    const interval = Number(this.dataset.interval) || 5000;

    this.timer = window.setInterval(() => {
      const nextIndex = this.currentIndex >= this.cards.length - 1 ? 0 : this.currentIndex + 1;
      this.goTo(nextIndex);
    }, interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('customer-result-cards')) {
  customElements.define('customer-result-cards', CustomerResultCards);
}
